import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import { ArrowRight, ExternalLink, Mail, Phone, ShoppingCart, CheckCircle2 } from "lucide-react";
import { VIVE_SHOPS } from "@/data/viveHealthCatalog";

export default function ViveHealthShopPage() {
  const { shopSlug } = useParams();
  const shop = useMemo(() => VIVE_SHOPS[shopSlug], [shopSlug]);

  if (!shop) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicSiteHeader />
        <MarketplaceCategoryNav currentCategory="medical-devices" />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Shop not found</h1>
          <p className="text-slate-600 mb-6">The Vive Health shop you&rsquo;re looking for doesn&rsquo;t exist yet.</p>
          <Link to="/marketplace/medical-devices" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-bold" data-testid="vive-shop-back-to-medical-devices">
            Back to Medical Devices <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`vive-shop-page-${shop.slug}`}>
      <PublicSiteHeader />
      <MarketplaceCategoryNav currentCategory="medical-devices" />
      <MarketplaceBreadcrumb
        trail={[
          { label: "Medical Devices", to: "/marketplace/medical-devices" },
          { label: shop.name },
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cyan-50 via-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[3px] text-cyan-700 mb-4">Vive Health &middot; B2B Catalog Partner</p>
              <h1 className="text-4xl lg:text-5xl font-black text-slate-900 leading-[1.05] tracking-tight mb-4" data-testid="vive-shop-title">
                {shop.name}
              </h1>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">{shop.tagline}</p>
              <p className="text-sm text-slate-600 mb-8">
                A curated selection of Vive Health SKUs available through Sterile Mate &mdash; with certified small business-certified purchasing, same-day quoting, and hospital-friendly Net-30 terms.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a
                  href={`mailto:orders@sterilemate.com?subject=Vive%20Health%20quote%20-%20${encodeURIComponent(shop.name)}`}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-black text-sm transition-all shadow-sm"
                  data-testid="vive-shop-quote-cta"
                >
                  <Mail className="w-4 h-4" /> Request a quote
                </a>
                {shop.externalCollection && (
                  <a
                    href={shop.externalCollection}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 hover:border-cyan-600 bg-white text-slate-900 text-sm font-bold transition-all"
                    data-testid="vive-shop-external-link"
                  >
                    Full Vive catalog <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white">
              <img src={shop.heroImage} alt={shop.name} className="w-full h-64 lg:h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="text-2xl font-black text-slate-900">Featured products <span className="text-slate-500 font-medium">&middot; {shop.products.length} in stock</span></h2>
          <p className="text-xs text-slate-500">MSRP shown &middot; B2B pricing on request</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {shop.products.map((p) => (
            <div
              key={p.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:ring-2 hover:ring-cyan-500/40 hover:shadow-lg transition-all"
              data-testid={`vive-product-${p.id}`}
            >
              <div className="aspect-square bg-slate-50">
                <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
              </div>
              <div className="p-5">
                <p className="text-[10px] font-black uppercase tracking-[2px] text-cyan-700 mb-2">SKU {p.sku}</p>
                <h3 className="text-lg font-bold text-slate-900 leading-snug mb-3">{p.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{p.summary}</p>
                <ul className="space-y-1.5 mb-4">
                  {p.features.slice(0, 4).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">MSRP</p>
                    <p className="text-xl font-black text-slate-900">${p.msrp.toFixed(2)}</p>
                  </div>
                  <a
                    href={`mailto:orders@sterilemate.com?subject=Quote%20-%20${encodeURIComponent(p.name)}%20(${p.sku})`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-black text-xs transition-all"
                    data-testid={`vive-product-quote-${p.id}`}
                  >
                    <ShoppingCart className="w-3.5 h-3.5" /> Quote
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA STRIP */}
        <div className="mt-14 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl border border-cyan-200 p-8 lg:p-10 text-center">
          <h3 className="text-2xl lg:text-3xl font-black text-slate-900 mb-3">Don&rsquo;t see the exact SKU?</h3>
          <p className="text-slate-700 max-w-2xl mx-auto mb-6">
            The Vive Health catalog has hundreds of SKUs across all 8 shops. We can source any Vive Health product &mdash; drop us the model or a link and we&rsquo;ll come back with hospital-friendly Net-30 pricing.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="mailto:orders@sterilemate.com?subject=Vive%20Health%20custom%20quote"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-black text-sm transition-all shadow-sm"
              data-testid="vive-shop-bottom-quote"
            >
              <Mail className="w-4 h-4" /> Email a custom list
            </a>
            <a
              href="tel:8009883892"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 hover:border-cyan-600 bg-white text-slate-900 text-sm font-bold transition-all"
              data-testid="vive-shop-bottom-phone"
            >
              <Phone className="w-4 h-4" /> 800.988.3892
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
